/**
 * The blackjack shoe, as one function both the table and the verifier call.
 *
 * The poker deck learned this lesson first: a shuffle a player cannot recompute is a
 * promise rather than a fact, and a shuffle recomputed by a SECOND implementation on
 * the verification page is a promise about two pieces of code agreeing. So there is
 * one function, `table.ts` deals from it, `/fair` replays it, and a divergence is
 * impossible rather than unlikely.
 *
 * ## The label, and why it is not just "deck"
 *
 * `deriveSeed(sessionSeed, "blackjack/deal/N")` is domain-separated from poker's
 * `hand:N`, and the RNG's own label is `blackjack` rather than `deck`. Both matter:
 * the first means a session seed used for both games cannot produce a blackjack shoe
 * that is a function of a poker deck, and the second means the two never consume the
 * same keystream bytes even from an identical derived seed.
 *
 * ## A shoe is not a deck
 *
 * It is `decks * 52` cards built as `i % 52`, so every card id appears once per deck.
 * That is worth stating because it breaks an assumption a 52-card deck lets you make:
 * a card id is NOT unique in a shoe, and any check written as "this card is not in
 * that list" will produce false alarms. Verify shoes by multiset, never by identity.
 */
import { shuffleInPlace, seedBytes } from "../deck";
import { CryptoRng, deriveSeed, type CryptoSeed } from "../rng";
import type { Card } from "../types";
import { shoeSize, type BlackjackRules } from "./rules";

/**
 * The label a given hand's shoe is derived under.
 *
 * ZERO-BASED, deliberately, because the poker engine's is: hand one of a session is
 * `hand:0` there and `blackjack/deal/0` here. The table's own counter is one-based and
 * subtracts before calling — which looks like an off-by-one and is the opposite. If
 * the two games disagreed on this, `/fair` would need two different mental models for
 * "hand 1" and would eventually show somebody the wrong shoe.
 */
export const shoeLabel = (handNo: number): string => `blackjack/deal/${handNo}`;

/** The seed hand `handNo` is dealt from, given the session seed. */
export function shoeSeed(sessionSeed: CryptoSeed, handNo: number): Uint8Array {
  return deriveSeed(seedBytes(sessionSeed), shoeLabel(handNo));
}

/** A shuffled shoe for one hand, from the session seed. */
export function blackjackShoe(
  sessionSeed: CryptoSeed, handNo: number, rules: BlackjackRules,
): Card[] {
  const n = shoeSize(rules);
  const shoe: Card[] = Array.from({ length: n }, (_, i) => i % 52);
  shuffleInPlace(shoe, new CryptoRng(shoeSeed(sessionSeed, handNo), "blackjack"));
  return shoe;
}

/**
 * Is this a legal shoe for these rules?
 *
 * By multiset, for the reason in the header: every rank-suit appears exactly `decks`
 * times, and nothing else appears at all.
 */
export function isLegalShoe(shoe: readonly Card[], rules: BlackjackRules): boolean {
  if (shoe.length !== shoeSize(rules)) return false;
  const counts = new Int32Array(52);
  for (const c of shoe) {
    if (!Number.isInteger(c) || c < 0 || c > 51) return false;
    counts[c]!++;
  }
  for (let i = 0; i < 52; i++) if (counts[i] !== rules.decks) return false;
  return true;
}
